import { Typography } from 'ui/typography';
import { Loader } from 'ui/loader';
import { Checkbox } from 'ui/checkbox';

import Button from 'components/partials/button/button';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { useGlobalSettingsPage } from 'containers/admin/global-settings/use-global-settings-page';
import type { GlobalSettingsRow } from 'containers/admin/global-settings/use-global-settings-page';

import { CohortStageSettingDtoStage as CohortStage } from 'api/generated/schemas';
import type { CohortStageSettingDtoStage } from 'api/generated/schemas';

import Icons from 'assets/icons';

type StageKey = 'admit' | 'netDeposit';

interface StageColumn {
  key: StageKey;
  stage: CohortStageSettingDtoStage;
  title: string;
  label: string;
  testIdPrefix: string;
}

const STAGE_COLUMNS: StageColumn[] = [
  {
    key: 'admit',
    stage: CohortStage.ADMIT,
    title: 'ADMITS',
    label: 'Admits',
    testIdPrefix: 'admit',
  },
  {
    key: 'netDeposit',
    stage: CohortStage.NET_DEPOSIT,
    title: 'NET DEPOSITS',
    label: 'Net Deposits',
    testIdPrefix: 'net-deposit',
  },
];

const headerCellClass =
  'sticky top-0 z-10 bg-navy-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-navy-100';
const stageCellClass = 'w-28 sm:w-36 text-center';

const HELPER_TEXT =
  'Choose which cohorts show comparison data for Admits and Net Deposits in monitoring. Unchecked cohorts are hidden.';

const GlobalSettingsPage = (): React.JSX.Element => {
  const {
    rows,
    isLoading,
    isError,
    isEmpty,
    isDirty,
    isSaving,
    unsavedChangesCount,
    handleToggle,
    handleToggleAll,
    handleSave,
    handleCancel,
  } = useGlobalSettingsPage();

  const renderStageHeader = ({ key, stage, title, label }: StageColumn) => {
    const checkedCount = rows.filter((row) => row[key]).length;
    const allChecked = rows.length > 0 && checkedCount === rows.length;

    return (
      <th
        key={key}
        scope="col"
        className={`${headerCellClass} ${stageCellClass}`}
      >
        <div className="flex items-center justify-center gap-1">
          <span>{title}</span>
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={checkedCount > 0 && !allChecked}
            disabled={isSaving}
            onChange={() => handleToggleAll(stage)}
            aria-label={`Show all cohorts for ${label}`}
            data-testid={`global-settings-toggle-all-${key}`}
          />
        </div>
      </th>
    );
  };

  const renderRow = (row: GlobalSettingsRow) => (
    <tr
      key={row.cohortName}
      className="border-b border-navy-100 last:border-b-0 hover:bg-navy-50"
    >
      <th
        scope="row"
        className="max-w-0 truncate px-4 py-3 text-left text-sm font-normal text-gray-900"
        title={row.cohortName}
      >
        {row.cohortName}
      </th>
      {STAGE_COLUMNS.map(({ key, stage, label, testIdPrefix }) => (
        <td key={key} className={`px-4 py-1 ${stageCellClass}`}>
          <Checkbox
            checked={row[key]}
            disabled={isSaving}
            onChange={() => handleToggle(row.cohortName, stage)}
            aria-label={`Show ${row.cohortName} cohort for ${label}`}
            data-testid={`global-settings-${testIdPrefix}-${row.cohortName}`}
          />
        </td>
      ))}
    </tr>
  );

  const renderContent = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <Loader loading={true} fullScreen={false} />
        </div>
      );
    }

    if (isError || isEmpty) {
      return (
        <EmptySpaceContent
          customClass="min-h-64 justify-center"
          image={<Icons.EmptyImageAll customClass="size-20" />}
          text={
            isError
              ? 'Failed to load global settings.'
              : 'There are no cohorts to show yet.'
          }
        />
      );
    }

    return (
      <table className="w-full table-fixed border-separate border-spacing-0">
        <caption className="sr-only">Cohort visibility</caption>
        <thead>
          <tr>
            <th scope="col" className={`${headerCellClass} text-left`}>
              COHORT
            </th>
            {STAGE_COLUMNS.map(renderStageHeader)}
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    );
  };

  return (
    <ResponsiveWrapper customClass="flex flex-col">
      <div className="my-4">
        <Typography
          data-testid="admin-global-settings-heading"
          variant="h1"
          sx={{ flexShrink: 0 }}
        >
          Global settings
        </Typography>
      </div>

      <section className="mb-8 flex w-full max-w-[700px] flex-col rounded-lg border border-navy-100 bg-white shadow-sm">
        <p className="px-4 pt-4 text-sm text-gray-600">{HELPER_TEXT}</p>
        <div className="mt-4 border-t border-navy-100">{renderContent()}</div>
        <div
          data-testid="global-settings-footer"
          className="sticky bottom-0 z-10 flex items-center justify-between gap-4 rounded-b-lg border-t border-navy-100 bg-white px-4 py-3"
        >
          {isDirty && (
            <span
              role="status"
              data-testid="global-settings-unsaved-count"
              className="text-sm text-gray-600"
            >
              {`${unsavedChangesCount} unsaved ${
                unsavedChangesCount === 1 ? 'change' : 'changes'
              }`}
            </span>
          )}
          <div className="ml-auto flex">
            <Button
              type="button"
              variant="reserve-text"
              size="md"
              disabled={!isDirty || isSaving}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="brand-filled"
              size="md"
              disabled={!isDirty}
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </section>
    </ResponsiveWrapper>
  );
};

export default GlobalSettingsPage;
